// ===========================================
// Hybrid Individual Pre-Book Dialog
// ===========================================
// Standalone dialog version of the admin pre-book wizard
// (see AdminHybridPreBookingPage for the dedicated-page version).
// Used by the admin Calendar toolbar.

import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  AccessTime as TimeIcon,
  Event as EventIcon,
  History as HistoryIcon,
  Lock as LockIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import {
  useAdminAvailableSlots,
  useCreateAdminPreBooking,
  useHybridBookingStatus,
  useLessonBookings,
} from '../../hooks/useHybridBooking';
import { useLessons } from '../../hooks/useLessons';
import { useTerms } from '../../hooks/useAdmin';
import { useStudents } from '../../hooks/useUsers';
import { TimeSlot } from '../../api/hybridBooking.api';
import { Student } from '../../api/users.api';
import { LessonEnrollment } from '../../api/lessons.api';
import WeekTilePicker from '../booking/WeekTilePicker';
import { startsAfterWeek } from '../../utils/occurrenceRoster';
import { parseUTCDate } from '../../utils/dateUtils';
import { slotDayIsPastForSchool } from '../../utils/slotFilters';
import { useSchoolTimezone } from '../../contexts/TenantContext';
import { getRunningTerm, getDefaultTermId } from '../../utils/bookableTerms';
import { getPreBookableTerms } from '../../utils/termLock';
import { useTermLock } from '../../hooks/useTermLock';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function formatSlotDate(date: string | Date): string {
  const d = typeof date === 'string' ? parseUTCDate(date) : date;
  return format(d, 'EEE d MMM');
}

// ===========================================
// SLOT PICKER (mirrors AdminHybridPreBookingPage's local SlotPicker)
// ===========================================

interface SlotPickerProps {
  slots: TimeSlot[];
  selected: TimeSlot | null;
  onSelect: (slot: TimeSlot) => void;
  isLoading: boolean;
}

function SlotPicker({ slots, selected, onSelect, isLoading }: SlotPickerProps) {
  if (isLoading) {
    return (
      <Grid container spacing={1}>
        {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
          <Grid item xs={6} sm={4} md={3} key={i}>
            <Skeleton variant="rectangular" height={56} />
          </Grid>
        ))}
      </Grid>
    );
  }

  if (slots.length === 0) {
    return (
      <Typography color="text.secondary" variant="body2">
        No available slots for this week.
      </Typography>
    );
  }

  return (
    <Grid container spacing={1}>
      {slots.map((slot, idx) => {
        const isSelected =
          selected?.dateKey === slot.dateKey && selected?.startTime === slot.startTime;
        const isAvailable = slot.isAvailable !== false;

        return (
          <Grid item xs={6} sm={4} md={3} key={`${slot.dateKey}-${slot.startTime}-${idx}`}>
            <Box
              onClick={() => isAvailable && onSelect(slot)}
              sx={{
                p: 1.5,
                border: '2px solid',
                borderColor: isSelected
                  ? 'primary.main'
                  : isAvailable
                  ? 'divider'
                  : 'action.disabled',
                borderRadius: 1,
                cursor: isAvailable ? 'pointer' : 'not-allowed',
                opacity: isAvailable ? 1 : 0.5,
                bgcolor: isSelected ? 'primary.50' : 'background.paper',
                '&:hover': isAvailable ? { borderColor: 'primary.main' } : {},
                textAlign: 'center',
              }}
            >
              <Typography variant="caption" color="text.secondary" display="block">
                {formatSlotDate(slot.dateKey)}
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {slot.startTime}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                –{slot.endTime}
              </Typography>
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );
}

// ===========================================
// DIALOG
// ===========================================

export interface HybridPreBookDialogPrefill {
  date?: Date;
}

export interface HybridPreBookDialogProps {
  open: boolean;
  onClose: () => void;
  prefill?: HybridPreBookDialogPrefill;
}

export default function HybridPreBookDialog({
  open,
  onClose,
  prefill,
}: HybridPreBookDialogProps) {
  const [selectedLessonId, setSelectedLessonId] = useState<string | null>(null);
  // selectedWeek = the 1:1 CLINIC week the booking is FOR (step 2, individual
  // weeks only). scheduledWeek = the term week the slot is actually scheduled
  // into (step 3, any week; defaults to the clinic week). The saved booking's
  // weekNumber follows scheduledWeek, matching the 1:1 clinic reschedule.
  const [selectedWeek, setSelectedWeek] = useState<number | null>(null);
  const [scheduledWeek, setScheduledWeek] = useState<number | null>(null);
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null);
  const [selectedFamilyId, setSelectedFamilyId] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [selectedTermId, setSelectedTermId] = useState<string | null>(null);

  const { data: bookingStatus, isLoading: statusLoading } = useHybridBookingStatus();
  const { data: allLessons, isLoading: lessonsLoading } = useLessons();
  const { data: terms } = useTerms();
  // Terms an admin can pre-book: active and not LOCKED (mirrors AdminHybridPreBookingPage). A
  // term that has only finished stays offered, labelled "(Ended)", until the NEXT term starts;
  // pre-booking is also for setting up a term ahead, so the holiday-gap upcoming term is selectable.
  const schoolTimeZone = useSchoolTimezone();
  const { isLocked, schoolToday } = useTermLock(terms);
  const bookableTerms = getPreBookableTerms(terms, (t) => isLocked(t.id));
  const runningTerm = getRunningTerm(bookableTerms, new Date(), schoolTimeZone);
  // Launched from a calendar cell: prefer the bookable term CONTAINING the
  // clicked date (may be a future term). Otherwise — or when the clicked date
  // falls outside every term — default to running-else-nearest-upcoming.
  const prefillTerm = prefill?.date
    ? bookableTerms.find(
        (t) =>
          new Date(t.startDate) <= prefill.date! && new Date(t.endDate) >= prefill.date!,
      )
    : undefined;
  const effectiveTermId = selectedTermId ?? prefillTerm?.id ?? getDefaultTermId(bookableTerms, new Date(), schoolTimeZone);
  const effectiveTerm = bookableTerms.find((t) => t.id === effectiveTermId) ?? null;
  const lessons = allLessons?.filter(
    (l) => l.HybridLessonPattern != null && l.termId === effectiveTermId,
  );
  const { data: allStudents } = useStudents();

  const selectedLesson = lessons?.find((l) => l.id === selectedLessonId);
  const individualWeeks =
    (selectedLesson?.HybridLessonPattern?.individualWeeks ?? []) as number[];

  // Slot grid follows the SCHEDULED week (step 3), so the admin can move the
  // booking into any term week's availability — mirrors the reschedule modal.
  const { data: weekSlots, isLoading: slotsLoading } = useAdminAvailableSlots(
    selectedLessonId ?? '',
    scheduledWeek ?? 0,
  );
  // ⚠️ PAIRED WITH `allowPast` ON THE WEEK PICKER BELOW — they must move together.
  // Admin pre-booking is the ONE path documented as existing for back-dating
  // ("a back-dated pre-booking is the documented PURPOSE of this path" —
  // hybridBooking.service.ts, createAdminPreBooking), so MNM can record a 1:1
  // that was arranged off-platform and never entered. The server honours it:
  // `getAdminAvailableSlots` has NO past filter (the one in
  // `generateOpenClinicSlots` serves the PARENT flow and is untouched), the
  // family confirmation is suppressed for a past date, and a CLINIC_PRE_BOOKED
  // audit row is written. Dropping elapsed days here would leave a selectable
  // week with zero slots — a dead end that reads as a loading bug.
  // (MNM request 4, 2026-09-19.)
  const bookableSlots = weekSlots ?? [];

  // Is the CHOSEN slot in the past? Keyed on the selected slot's own date, not
  // the week: a part-past week offers both elapsed and remaining days, so the
  // week alone would warn on an ordinary forward booking.
  //
  // ⚠️ Uses the SCHOOL's day, not `startOfTodayUTC()` — see
  // `slotDayIsPastForSchool`. The backend decides the same question with
  // `schoolTodayUtcMidnight`, and the two disagree for ~10 hours every Sydney
  // morning; the first cut of this notice was silent in exactly that window.
  const selectedSlotIsPast = useMemo(
    () => slotDayIsPastForSchool(selectedSlot, schoolTimeZone),
    [selectedSlot, schoolTimeZone]
  );
  // All active bookings for this lesson (any week). Resolved by CLINIC week
  // (clinicWeekNumber ?? weekNumber) client-side, this drives the step-2
  // reserved-count chips and per-student greying — so a student booked FOR a
  // clinic week is matched even when scheduled into a different week. The slot
  // grid's taken/free state comes from weekSlots' server-computed isAvailable.
  const { data: lessonBookings } = useLessonBookings(selectedLessonId ?? '', {});

  const createPreBooking = useCreateAdminPreBooking();

  // Active bookings FOR the selected CLINIC week (clinicWeekNumber ?? weekNumber).
  const clinicWeekBookings = (lessonBookings ?? []).filter(
    (b) => b.status !== 'CANCELLED' && (b.clinicWeekNumber ?? b.weekNumber) === selectedWeek,
  );
  const enrolledStudentIds = new Set(
    clinicWeekBookings.map((b) => b.Student?.id).filter(Boolean),
  );
  const preBookedStudentIds = new Set(
    clinicWeekBookings
      .filter((b) => b.isAdminPreBooking)
      .map((b) => b.Student?.id)
      .filter(Boolean),
  );

  const enrolledStudents: Student[] =
    allStudents?.filter((s: Student) =>
      selectedLesson?.LessonEnrollment?.some(
        (e: LessonEnrollment) => e.studentId === s.id && e.isActive,
      ),
    ) ?? [];

  // Reset all wizard state on close
  const resetState = () => {
    setSelectedTermId(null);
    setSelectedLessonId(null);
    setSelectedWeek(null);
    setScheduledWeek(null);
    setSelectedStudentId(null);
    setSelectedFamilyId(null);
    setSelectedSlot(null);
    setAdminNotes('');
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  // Best-effort prefill: when the calendar passes a date and a hybrid lesson
  // happens on that day-of-week, auto-select it. If multiple match, leave
  // the user to pick. This runs only when the dialog opens with a prefill.
  useEffect(() => {
    if (!open || !prefill?.date || !lessons || lessons.length === 0) return;
    if (selectedLessonId) return; // user already picked one
    const dow = prefill.date.getDay();
    const matches = lessons.filter((l) => l.dayOfWeek === dow);
    if (matches.length === 1) {
      setSelectedLessonId(matches[0].id);
    }
    // No-op if 0 or >1 match — user picks normally.
  }, [open, prefill?.date, lessons, selectedLessonId]);

  const handleLessonSelect = (lessonId: string) => {
    setSelectedLessonId(lessonId);
    setSelectedWeek(null);
    setScheduledWeek(null);
    setSelectedStudentId(null);
    setSelectedFamilyId(null);
    setSelectedSlot(null);
  };

  const handleTermChange = (termId: string) => {
    setSelectedTermId(termId);
    // The selected class belongs to the old term — clear the whole selection.
    setSelectedLessonId(null);
    setSelectedWeek(null);
    setScheduledWeek(null);
    setSelectedStudentId(null);
    setSelectedFamilyId(null);
    setSelectedSlot(null);
  };

  const handleWeekSelect = (week: number) => {
    setSelectedWeek(week);
    setScheduledWeek(null);
    setSelectedStudentId(null);
    setSelectedFamilyId(null);
    setSelectedSlot(null);
  };

  const handleStudentSelect = (studentId: string, familyId: string) => {
    setSelectedStudentId(studentId);
    setSelectedFamilyId(familyId);
    // Default the scheduled week to the clinic week; admin can move it in step 3.
    setScheduledWeek(selectedWeek);
    setSelectedSlot(null);
  };

  // Step 3: admin picks which term week the slot is actually scheduled into.
  const handleScheduledWeekSelect = (week: number) => {
    setScheduledWeek(week);
    setSelectedSlot(null);
  };

  const handlePreBook = () => {
    if (
      !selectedLessonId ||
      !selectedWeek ||
      !scheduledWeek ||
      !selectedStudentId ||
      !selectedFamilyId ||
      !selectedSlot
    )
      return;

    const dateKey =
      selectedSlot.dateKey ??
      (selectedSlot.date ? new Date(selectedSlot.date).toISOString().split('T')[0] : '');
    createPreBooking.mutate(
      {
        lessonId: selectedLessonId,
        studentId: selectedStudentId,
        familyId: selectedFamilyId,
        // weekNumber follows the SCHEDULED week (the actual date), matching the
        // 1:1 clinic reschedule write-path.
        weekNumber: scheduledWeek,
        // clinicWeekNumber = the 1:1 clinic week the booking is FOR (step 2).
        clinicWeekNumber: selectedWeek,
        scheduledDate: `${dateKey}T00:00:00.000Z`,
        startTime: selectedSlot.startTime,
        endTime: selectedSlot.endTime,
        adminNotes: adminNotes.trim() || undefined,
      },
      {
        onSuccess: () => {
          handleClose();
        },
      },
    );
  };

  const hasHybridLessons = (bookingStatus?.totalHybridLessons ?? 0) > 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>Pre-Book Hybrid Individual Slot</DialogTitle>
      <DialogContent dividers>
        {!hasHybridLessons && !statusLoading && (
          <Alert severity="info" sx={{ mb: 3 }}>
            No hybrid lessons found. Create a hybrid lesson first.
          </Alert>
        )}
        {hasHybridLessons && !statusLoading && bookableTerms.length === 0 && (
          <Alert severity="info" sx={{ mb: 3 }}>
            No current or upcoming terms. Set up a term (with future dates) to pre-book its clinics. Ended terms are closed to edits on purpose — records for a term must be tidied up before the next one starts.
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* PANEL 1: Lesson Selector */}
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%' }}>
              <CardHeader
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EventIcon color="primary" fontSize="small" />
                    <Typography variant="h6">1. Select Class</Typography>
                  </Box>
                }
              />
              <Divider />
              <CardContent sx={{ p: 0 }}>
                {bookableTerms.length > 0 && (
                  <Box sx={{ p: 2, pb: 1 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="prebook-dialog-term-label">Term</InputLabel>
                      <Select
                        labelId="prebook-dialog-term-label"
                        label="Term"
                        value={effectiveTermId ?? ''}
                        onChange={(e) => handleTermChange(e.target.value)}
                      >
                        {bookableTerms.map((t) => (
                          <MenuItem key={t.id} value={t.id}>
                            {t.name}
                            {runningTerm?.id === t.id
                              ? ' (current)'
                              : new Date(t.endDate) < schoolToday
                                ? ' (Ended)'
                                : ''}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                )}
                {lessonsLoading || statusLoading ? (
                  <Box sx={{ p: 2 }}>
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} height={60} sx={{ mb: 1 }} />
                    ))}
                  </Box>
                ) : !lessons || lessons.length === 0 ? (
                  <Typography color="text.secondary" sx={{ p: 2 }}>
                    No hybrid lessons in this term.
                  </Typography>
                ) : (
                  lessons.map((lesson) => {
                    const isSelected = selectedLessonId === lesson.id;
                    const individualWks =
                      (lesson.HybridLessonPattern?.individualWeeks ?? []) as number[];
                    const teacher = lesson.Teacher ?? lesson.Instructor;
                    return (
                      <Box
                        key={lesson.id}
                        onClick={() => handleLessonSelect(lesson.id)}
                        sx={{
                          px: 2,
                          py: 1.5,
                          cursor: 'pointer',
                          borderLeft: isSelected
                            ? '3px solid'
                            : '3px solid transparent',
                          borderLeftColor: isSelected
                            ? 'primary.main'
                            : 'transparent',
                          bgcolor: isSelected ? 'primary.50' : 'transparent',
                          '&:hover': { bgcolor: 'action.hover' },
                          borderBottom: '1px solid',
                          borderBottomColor: 'divider',
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight={600}>
                          {lesson.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {lesson.dayOfWeek != null
                            ? DAY_NAMES[lesson.dayOfWeek]
                            : 'TBD'}{' '}
                          • {lesson.startTime ?? 'TBD'}–{lesson.endTime ?? 'TBD'}
                        </Typography>
                        <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {teacher && (
                            <Chip
                              label={`${teacher.User.firstName} ${teacher.User.lastName}`}
                              size="small"
                              sx={{
                                fontSize: '0.65rem',
                                height: 18,
                                ...(teacher.color
                                  ? {
                                      backgroundColor: teacher.color,
                                      color: 'white',
                                    }
                                  : {}),
                              }}
                            />
                          )}
                          <Chip
                            label={`${individualWks.length} individual wk${individualWks.length !== 1 ? 's' : ''}`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.65rem', height: 18 }}
                          />
                        </Box>
                      </Box>
                    );
                  })
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* PANELS 2 & 3 */}
          <Grid item xs={12} md={8}>
            <Stack spacing={3}>
              {/* Panel 2: Week + Student */}
              <Card>
                <CardHeader
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon color="primary" fontSize="small" />
                      <Typography variant="h6">2. Select Week & Student</Typography>
                    </Box>
                  }
                />
                <Divider />
                <CardContent>
                  {!selectedLessonId ? (
                    <Typography color="text.secondary" variant="body2">
                      Select a lesson first.
                    </Typography>
                  ) : (
                    <>
                      {/* Step 2 picks the 1:1 CLINIC week the booking is FOR
                          (individual weeks only) and gates which students are
                          eligible. The actual scheduled date/week is chosen in
                          step 3, where the admin may move it into any term week. */}
                      <Typography variant="subtitle2" gutterBottom>
                        Individual Weeks
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        {individualWeeks.map((week) => {
                          // Count active pre-bookings FOR this clinic week
                          // (clinicWeekNumber ?? weekNumber), regardless of the
                          // week they're scheduled into.
                          const weekPreBookings = (lessonBookings ?? []).filter(
                            (b) =>
                              b.isAdminPreBooking &&
                              b.status !== 'CANCELLED' &&
                              (b.clinicWeekNumber ?? b.weekNumber) === week,
                          );
                          return (
                            <Chip
                              key={week}
                              label={`Week ${week}${weekPreBookings.length > 0 ? ` (${weekPreBookings.length} reserved)` : ''}`}
                              onClick={() => handleWeekSelect(week)}
                              color={selectedWeek === week ? 'primary' : 'default'}
                              variant={selectedWeek === week ? 'filled' : 'outlined'}
                              icon={
                                weekPreBookings.length > 0 ? <LockIcon /> : undefined
                              }
                              sx={{ cursor: 'pointer' }}
                            />
                          );
                        })}
                      </Box>

                      {selectedWeek && (
                        <>
                          <Typography variant="subtitle2" gutterBottom>
                            Enrolled Students — Week {selectedWeek}
                          </Typography>
                          {enrolledStudents.length === 0 ? (
                            <Typography color="text.secondary" variant="body2">
                              No enrolled students.
                            </Typography>
                          ) : (
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                              }}
                            >
                              {enrolledStudents.map((student) => {
                                const hasBooking = enrolledStudentIds.has(student.id);
                                const hasPreBooking = preBookedStudentIds.has(student.id);
                                const isSelected = selectedStudentId === student.id;
                                // A child who joins the class AFTER this clinic week can't be
                                // booked into it (the server 400s too) — show when they start.
                                const startsInWeek = startsAfterWeek(
                                  selectedLesson,
                                  selectedLesson?.LessonEnrollment,
                                  student.id,
                                  selectedWeek,
                                );
                                const isSelectable =
                                  !hasBooking && !hasPreBooking && startsInWeek == null;

                                return (
                                  <Box
                                    key={student.id}
                                    onClick={() => {
                                      if (isSelectable) {
                                        handleStudentSelect(
                                          student.id,
                                          student.familyId ?? '',
                                        );
                                      }
                                    }}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1,
                                      px: 1.5,
                                      py: 1,
                                      borderRadius: 1,
                                      border: '1px solid',
                                      borderColor: isSelected
                                        ? 'primary.main'
                                        : 'divider',
                                      bgcolor: isSelected
                                        ? 'primary.50'
                                        : hasBooking || hasPreBooking
                                        ? 'action.disabledBackground'
                                        : 'background.paper',
                                      cursor: isSelectable ? 'pointer' : 'default',
                                      opacity:
                                        hasBooking || hasPreBooking ? 0.7 : 1,
                                      '&:hover': isSelectable
                                        ? { borderColor: 'primary.main' }
                                        : {},
                                    }}
                                  >
                                    <Typography variant="body2" sx={{ flex: 1 }}>
                                      {student.firstName} {student.lastName}
                                    </Typography>
                                    {hasPreBooking && (
                                      <Chip
                                        label="Pre-reserved"
                                        size="small"
                                        icon={<LockIcon />}
                                        color="info"
                                        variant="outlined"
                                        sx={{ fontSize: '0.65rem', height: 20 }}
                                      />
                                    )}
                                    {hasBooking && !hasPreBooking && (
                                      <Chip
                                        label="Booked"
                                        size="small"
                                        color="success"
                                        variant="outlined"
                                        sx={{ fontSize: '0.65rem', height: 20 }}
                                      />
                                    )}
                                    {startsInWeek != null && (
                                      <Chip
                                        label={`Starts Week ${startsInWeek}`}
                                        size="small"
                                        variant="outlined"
                                        sx={{ fontSize: '0.65rem', height: 20 }}
                                      />
                                    )}
                                  </Box>
                                );
                              })}
                            </Box>
                          )}
                        </>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Panel 3: Week + Slot Picker — admin chooses which term week the
                  slot is actually scheduled into (default = clinic week) and the
                  time slot, mirroring the 1:1 clinic reschedule modal. */}
              {selectedStudentId && selectedWeek && selectedLessonId && (
                <Card>
                  <CardHeader
                    title={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TimeIcon color="primary" fontSize="small" />
                        <Typography variant="h6">3. Select Date & Time Slot</Typography>
                      </Box>
                    }
                  />
                  <Divider />
                  <CardContent>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Booking for the <strong>Week {selectedWeek}</strong> 1:1 clinic.
                      Choose any week below to schedule it — defaults to the clinic week.
                    </Alert>
                    <Typography variant="subtitle2" gutterBottom>
                      Scheduled Week
                    </Typography>
                    {effectiveTerm ? (
                      <WeekTilePicker
                        termStartDate={effectiveTerm.startDate}
                        termEndDate={effectiveTerm.endDate}
                        selectedWeek={scheduledWeek ?? undefined}
                        onWeekSelect={handleScheduledWeekSelect}
                        currentWeek={selectedWeek ?? undefined}
                        occurrenceDayOfWeek={selectedLesson?.dayOfWeek ?? undefined}
                        // DESTINATION week — see the Alert above: "Choose any
                        // week below to schedule it". The admin picks any day
                        // the teacher works, so the lesson's own weekday must
                        // not decide whether the week has passed.
                        pastRule="week-end"
                        // ⚠️ Paired with `bookableSlots` above — see the note
                        // there. Unlocking one without the other yields either a
                        // locked week or an empty one.
                        allowPast
                      />
                    ) : (
                      <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
                        No current or upcoming terms.
                      </Typography>
                    )}
                    <Typography variant="subtitle2" gutterBottom>
                      Time Slot
                    </Typography>
                    <SlotPicker
                      slots={bookableSlots}
                      selected={selectedSlot}
                      onSelect={setSelectedSlot}
                      isLoading={slotsLoading}
                    />

                    {/* The audit consequence, stated where the choice is made.
                        A past booking records history rather than scheduling
                        something — say so before submit, not after the row
                        appears in the owner's report. */}
                    {selectedSlotIsPast && (
                      <Alert severity="warning" icon={<HistoryIcon />} sx={{ mt: 2 }}>
                        <Typography variant="body2">
                          This session has already taken place. Booking it records what happened —
                          the family is <strong>not</strong> sent a confirmation, and it will appear
                          in the <strong>retroactive edit report</strong> against your name.
                        </Typography>
                      </Alert>
                    )}

                    {selectedSlot && (
                      <Box sx={{ mt: 2 }}>
                        <TextField
                          label="Admin Notes (optional)"
                          multiline
                          rows={2}
                          fullWidth
                          value={adminNotes}
                          onChange={(e) => setAdminNotes(e.target.value)}
                          inputProps={{ maxLength: 1000 }}
                          size="small"
                          sx={{ mb: 2 }}
                        />
                        <Alert severity="info" sx={{ mb: 0 }}>
                          Selected:{' '}
                          <strong>{formatSlotDate(selectedSlot.dateKey)}</strong> at{' '}
                          <strong>
                            {selectedSlot.startTime}–{selectedSlot.endTime}
                          </strong>
                        </Alert>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              )}
            </Stack>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={createPreBooking.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={
            createPreBooking.isPending ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <LockIcon />
            )
          }
          onClick={handlePreBook}
          disabled={
            createPreBooking.isPending ||
            !selectedLessonId ||
            !scheduledWeek ||
            !selectedStudentId ||
            !selectedFamilyId ||
            !selectedSlot
          }
        >
          Confirm Pre-Booking
        </Button>
      </DialogActions>
    </Dialog>
  );
}
